import { useState } from "react";
import { useEntry, Loading, ErrorBox, Chip, PROV_META, PROV_BAR, IntegrityNote } from "./entryShared";
import { CaretDown, CaretUp, ShieldCheck, SealQuestion } from "@phosphor-icons/react";

const CONF = { high: "text-emerald-700", medium: "text-amber-600", low: "text-red-600" };

const GField = ({ label, value }) => (
  <div>
    <p className="text-[9px] uppercase tracking-widest text-slate-400">{label}</p>
    <p className={`text-[11px] ${value ? "text-slate-700" : "text-slate-400"}`}>{value || "Not documented"}</p>
  </div>
);

const StageGuardrail = ({ s }) => (
  <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-4" data-testid={`guardrail-${s.id}`}>
    <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">Claim</p>
    <p className="text-xs text-slate-800 font-semibold mt-0.5">{s.claim || "Not documented"}</p>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2 mt-3">
      <GField label="Evidence source" value={s.evidence_source} />
      <GField label="Source date" value={s.source_date} />
      <GField label="Jurisdiction" value={s.jurisdiction} />
      <div>
        <p className="text-[9px] uppercase tracking-widest text-slate-400">Confidence · Last verified</p>
        <p className="text-[11px]"><span className={`font-bold uppercase ${CONF[s.confidence] || "text-slate-400"}`}>{s.confidence || "unknown"}</span>
          <span className="text-slate-500"> · {s.last_verified || "never"}</span></p>
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2 mt-3">
      <GField label="Reasoning" value={s.reasoning} />
      <div>
        <p className="text-[9px] uppercase tracking-widest text-slate-400">Assumptions</p>
        {s.assumptions?.length ? (
          <ul className="mt-0.5 space-y-0.5">{s.assumptions.map((a, i) => (
            <li key={i} className="text-[11px] text-slate-700 flex gap-1.5"><span className="text-slate-300">—</span>{a}</li>))}
          </ul>
        ) : <p className="text-[11px] text-slate-400">None recorded</p>}
      </div>
    </div>
    <div className="mt-3 border-l-2 border-amber-500 bg-amber-50 px-3 py-2">
      <p className="text-[9px] uppercase tracking-widest text-amber-700 font-bold">Validation required</p>
      <p className="text-[11px] text-slate-700">{s.validation_required || "None specified"}</p>
    </div>
  </div>
);

const ConfidenceBar = ({ profile }) => (
  <div data-testid="pathway-confidence">
    <div className="flex h-3 w-full overflow-hidden">
      {Object.entries(profile.percentages).map(([k, v]) => v > 0 && (
        <div key={k} className={PROV_BAR[k]} style={{ width: `${v}%` }} title={`${PROV_META[k]?.label}: ${v}%`} />
      ))}
    </div>
    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
      {Object.entries(profile.percentages).map(([k, v]) => (
        <span key={k} className="flex items-center gap-1.5 text-[10px] text-white/70" data-testid={`confidence-${k}`}>
          <span className={`w-2 h-2 ${PROV_BAR[k]}`} />{PROV_META[k]?.label} <b className="text-white">{v}%</b>
        </span>
      ))}
    </div>
  </div>
);

const ValidationChecklist = ({ caseId }) => {
  const { data, error } = useEntry(caseId, "/validation");
  const [open, setOpen] = useState(null);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading label="Loading validation checklist…" />;
  return (
    <div className="mt-8" data-testid="validation-checklist">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
          <SealQuestion size={13} className="text-amber-600" /> Pathway validation checklist — questions, not invented answers
        </p>
        {data.total > 0 && (
          <p className="text-[11px] text-slate-500" data-testid="validation-counts">
            <b className="text-amber-700">{data.open} open</b> · {data.resolved} resolved · {data.total} total
          </p>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mt-1">{data.purpose_note}</p>
      <div className="mt-3 space-y-2">
        {(data.questions || []).map((q) => (
          <div key={q.id} className="border border-slate-200 bg-white" data-testid={`vq-${q.id}`}>
            <button onClick={() => setOpen(open === q.id ? null : q.id)} data-testid={`vq-toggle-${q.id}`}
              className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-slate-50">
              <p className="text-xs font-semibold text-slate-800">{q.question}</p>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${q.status === "open" ? "bg-amber-500 text-white" : "bg-emerald-700 text-white"}`}>{q.status}</span>
                {open === q.id ? <CaretUp size={12} /> : <CaretDown size={12} />}
              </div>
            </button>
            {open === q.id && (
              <div className="border-t border-slate-100 px-4 py-3 grid sm:grid-cols-2 gap-x-4 gap-y-2" data-testid={`vq-detail-${q.id}`}>
                <GField label="Why it matters" value={q.why_it_matters} />
                <GField label="Who should validate" value={q.who_should_validate} />
                <GField label="Evidence required" value={q.evidence_required} />
                <GField label="Impact if the answer changes" value={q.impact_if_answer_changes} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const CasePathway = ({ caseId }) => {
  const { data, error } = useEntry(caseId, "/pathway");
  const [open, setOpen] = useState(null);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  return (
    <div data-testid="case-pathway" className="mt-4">
      <div className="bg-slate-900 text-white px-5 py-4 mb-4" data-testid="pathway-note">
        <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold flex items-center gap-1.5">
          <ShieldCheck size={13} /> Evidence guardrail
        </p>
        <p className="text-[11px] text-white/70 mt-1 leading-relaxed">{data.guardrail_note}</p>
        <p className="text-[11px] text-white/50 mt-2 leading-relaxed">{data.pathway_note}</p>
        <div className="mt-4">
          <p className="text-[9px] uppercase tracking-widest text-white/40 mb-1.5">Pathway confidence — {data.confidence_profile?.validated_share ?? 0}% documented or precedent-supported</p>
          {data.confidence_profile && <ConfidenceBar profile={data.confidence_profile} />}
        </div>
      </div>
      <div className="space-y-3">
        {!data.stages?.length && (
          <p className="text-xs text-slate-400 border border-dashed border-slate-300 bg-white px-4 py-8 text-center" data-testid="pathway-empty">
            Pathway stages have not yet been developed for this case.</p>
        )}
        {(data.stages || []).map((s) => (
          <div key={s.id} className="border border-slate-200 bg-white" data-testid={`stage-${s.id}`}>
            <div className="flex">
              <div className="w-12 shrink-0 bg-slate-900 text-white flex items-start justify-center pt-4 font-heading font-bold">{s.order}</div>
              <div className="p-4 flex-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-bold text-slate-900">{s.name}</p>
                  <div className="flex items-center gap-2">
                    <Chip meta={PROV_META[s.provenance]} testId={`stage-prov-${s.id}`} />
                    <button onClick={() => setOpen(open === s.id ? null : s.id)} data-testid={`stage-guardrail-toggle-${s.id}`}
                      className="text-[10px] font-bold uppercase text-slate-500 hover:text-slate-900 flex items-center gap-1">
                      Evidence trail {open === s.id ? <CaretUp size={10} /> : <CaretDown size={10} />}
                    </button>
                  </div>
                </div>
                <p className="text-[11px] text-slate-600 mt-1">{s.objective}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2 mt-3 text-[11px]">
                  <div><p className="text-[9px] uppercase tracking-widest text-slate-400">Decision-maker</p><p className="text-slate-700">{s.decision_maker}</p></div>
                  <div><p className="text-[9px] uppercase tracking-widest text-slate-400">Stakeholders</p><p className="text-slate-700">{(s.stakeholders || []).join(", ")}</p></div>
                  <div><p className="text-[9px] uppercase tracking-widest text-slate-400">Evidence requirement</p><p className="text-slate-700">{s.evidence_requirement}</p></div>
                  <div><p className="text-[9px] uppercase tracking-widest text-slate-400">Deliverable</p><p className="text-slate-700">{s.deliverable}</p></div>
                  <div><p className="text-[9px] uppercase tracking-widest text-slate-400">Dependency · Duration</p><p className="text-slate-700">{s.dependency || "None"} · {s.estimated_duration}</p></div>
                  <div><p className="text-[9px] uppercase tracking-widest text-slate-400">Current readiness</p><p className="text-slate-700 uppercase font-bold">{s.readiness}</p></div>
                </div>
                <p className="text-[11px] text-amber-700 mt-2"><span className="font-bold">Evidence gap:</span> {s.evidence_gap}</p>
                <p className="text-[11px] text-emerald-700"><span className="font-bold">Recommended action:</span> {s.recommended_action}</p>
              </div>
            </div>
            {open === s.id && <StageGuardrail s={s} />}
          </div>
        ))}
      </div>
      <ValidationChecklist caseId={caseId} />
      <IntegrityNote text="MAI does not invent formal reimbursement pathways where none is documented — inferred stages are explicitly labelled and unresolved questions are surfaced for expert validation." />
    </div>
  );
};
